import {
  ChangeDetectorRef, Component, computed, effect, ElementRef, forwardRef,
  inject, input, model, signal, viewChild,
} from '@angular/core';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HlmIconComponent } from './icon.component';
import { CmtFlagComponent } from './flag.component';
import { HlmSpinnerComponent } from './misc';
import { cn } from './cn';

export interface HlmSelectOption<T = unknown> {
  value: T;
  label: string;
  icon?: string;
  /** ISO-3166 alpha-2 country code. Renders `<cmt-flag>` before the label. */
  flagIso?: string | null;
  disabled?: boolean;
}

@Component({
  selector: 'hlm-select',
  standalone: true,
  imports: [HlmIconComponent, CmtFlagComponent, HlmSpinnerComponent, CdkConnectedOverlay, CdkOverlayOrigin],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => HlmSelectComponent), multi: true },
  ],
  template: `
    <div class="relative w-full" (keydown)="onKeydown($event)">
      <button
        cdkOverlayOrigin
        #trigger="cdkOverlayOrigin"
        type="button"
        [class]="_btnClass()"
        [disabled]="disabled() || loading()"
        [attr.aria-expanded]="open()"
        [attr.aria-busy]="loading() || null"
        (click)="toggle()"
        (blur)="onTouched()"
      >
        <span class="flex min-w-0 flex-1 items-center gap-2 text-left">
          @if (loading()) {
            <span class="text-muted-foreground/70">{{ loadingPlaceholder() }}</span>
          } @else if (selectedOption(); as s) {
            @if (s.flagIso) { <cmt-flag [iso]="s.flagIso" /> }
            @if (s.icon) { <hlm-icon [name]="s.icon" size="0.9rem" class="text-muted-foreground" /> }
            <span class="truncate">{{ s.label }}</span>
          } @else {
            <span class="text-muted-foreground/70">{{ placeholder() }}</span>
          }
        </span>
        @if (loading()) {
          <hlm-spinner size="14" class="text-primary" />
        } @else {
          <hlm-icon name="lucideChevronsUpDown" size="0.9rem" class="text-muted-foreground" />
        }
      </button>

      <ng-template
        cdkConnectedOverlay
        [cdkConnectedOverlayOrigin]="trigger"
        [cdkConnectedOverlayOpen]="open()"
        [cdkConnectedOverlayWidth]="triggerWidth()"
        [cdkConnectedOverlayPositions]="overlayPositions"
        [cdkConnectedOverlayViewportMargin]="8"
        (overlayOutsideClick)="onOutsideClick($event)"
        (detach)="open.set(false)"
      >
        <div
          #panel
          (keydown)="onKeydown($event)"
          class="z-50 origin-top rounded-lg border border-border bg-popover shadow-[0_2px_4px_rgba(20,18,30,0.04),0_24px_48px_-12px_rgba(20,18,30,0.18)]"
          role="listbox"
        >
          @if (searchable()) {
            <div class="border-b border-border p-2">
              <input
                #searchInput
                type="text"
                class="h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/25"
                [placeholder]="searchPlaceholder()"
                [value]="query()"
                (input)="onQueryChange($event)"
                (click)="$event.stopPropagation()"
              />
            </div>
          }
          <div class="max-h-64 overflow-y-auto p-1">
            @if (filteredOptions().length === 0) {
              <div class="px-2.5 py-2 text-sm text-muted-foreground">No matches.</div>
            }
            @for (opt of filteredOptions(); track opt.value; let i = $index) {
              <button
                type="button"
                role="option"
                [attr.aria-selected]="opt.value === value()"
                [disabled]="opt.disabled"
                (click)="pick(opt)"
                [class]="_optClass(opt, i === highlighted())"
              >
                @if (opt.flagIso) { <cmt-flag [iso]="opt.flagIso" /> }
                @if (opt.icon) { <hlm-icon [name]="opt.icon" size="0.9rem" class="text-muted-foreground" /> }
                <span class="flex-1 text-left">{{ opt.label }}</span>
                @if (opt.value === value()) { <hlm-icon name="lucideCheck" size="0.85rem" class="text-primary" /> }
              </button>
            }
          </div>
        </div>
      </ng-template>
    </div>
  `,
})
export class HlmSelectComponent<T = unknown> implements ControlValueAccessor {
  readonly options = input<HlmSelectOption<T>[]>([]);
  readonly value = model<T | null>(null);
  readonly placeholder = input('Select…');
  readonly disabled = model(false);
  readonly userClass = input<string>('', { alias: 'class' });
  readonly searchable = input(false);
  readonly searchPlaceholder = input('Search…');
  /** Show a spinner instead of the chevron and disable interaction. */
  readonly loading = input(false);
  readonly loadingPlaceholder = input('Loading…');

  readonly open = signal(false);
  readonly highlighted = signal<number>(-1);
  readonly query = signal('');
  /** Trigger width (px) so the overlay panel matches the button. */
  readonly triggerWidth = signal(0);

  /** Prefer opening below the trigger, flip above when there's no room. */
  readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
  ];

  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private el = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  private onChange: (v: T | null) => void = () => {};
  onTouched: () => void = () => {};

  selectedOption = computed(() => this.options().find(o => o.value === this.value()));

  filteredOptions = computed(() => {
    if (!this.searchable()) return this.options();
    const q = this.query().trim().toLowerCase();
    if (!q) return this.options();
    return this.options().filter(o => o.label.toLowerCase().includes(q));
  });

  constructor() {
    // Autofocus the search input when the panel opens.
    effect(() => {
      if (this.open() && this.searchable()) {
        queueMicrotask(() => this.searchInput()?.nativeElement.focus());
      }
    });
  }

  onQueryChange(e: Event): void {
    this.query.set((e.target as HTMLInputElement).value);
    this.highlighted.set(0);
  }

  protected _btnClass = () =>
    cn(
      'flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-foreground',
      'focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/25',
      'disabled:cursor-not-allowed disabled:opacity-60 transition-colors',
      this.userClass(),
    );

  protected _optClass = (opt: HlmSelectOption<T>, highlighted: boolean) =>
    cn(
      'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-foreground transition-colors',
      'disabled:cursor-not-allowed disabled:opacity-50',
      opt.value === this.value() && 'bg-primary/10',
      highlighted && 'bg-muted',
      !opt.disabled && 'cursor-pointer hover:bg-muted',
    );

  toggle(): void {
    if (this.disabled() || this.loading()) return;
    this.open.update(v => !v);
    if (this.open()) {
      this.query.set('');
      const i = this.options().findIndex(o => o.value === this.value());
      this.highlighted.set(i >= 0 ? i : 0);
      const btn = this.el.nativeElement.querySelector('button');
      if (btn) this.triggerWidth.set(btn.offsetWidth);
    }
  }

  pick(opt: HlmSelectOption<T>): void {
    if (opt.disabled) return;
    this.value.set(opt.value);
    this.onChange(opt.value);
    this.open.set(false);
    this.query.set('');
  }

  onKeydown(e: KeyboardEvent): void {
    if (this.disabled()) return;
    if (!this.open()) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault(); this.toggle();
      }
      return;
    }
    const opts = this.filteredOptions();
    const len = opts.length;
    if (e.key === 'Escape') { this.open.set(false); e.preventDefault(); }
    else if (e.key === 'ArrowDown' && len) { this.highlighted.update(i => (i + 1) % len); e.preventDefault(); }
    else if (e.key === 'ArrowUp' && len) { this.highlighted.update(i => (i - 1 + len) % len); e.preventDefault(); }
    else if (e.key === 'Enter') {
      const opt = opts[this.highlighted()];
      if (opt) this.pick(opt);
      e.preventDefault();
    }
  }

  /**
   * The panel renders in a CDK overlay (body portal) so it escapes any
   * `overflow-hidden` ancestor (e.g. a dialog surface). CDK reports clicks
   * outside the panel here; ignore clicks on the trigger (toggle handles those).
   */
  onOutsideClick(e: MouseEvent): void {
    if (this.el.nativeElement.contains(e.target)) return;
    this.open.set(false);
    this.query.set('');
  }

  writeValue(v: T | null): void { this.value.set(v); this.cdr.markForCheck(); }
  registerOnChange(fn: (v: T | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void { this.disabled.set(d); }
}
